// Resolve a survey's variable map for the current open. The result feeds
// the logic engine (condition matching against `variableKey`) and the
// piping engine (`{{variable.<key>}}` substitution).
//
// Priority order, lowest → highest:
//   1. defaultValue from the variable definition
//   2. urlParam — current host page query string
//   3. hidden — host-supplied via Chirp.survey.open(id, { variables: {…} })
//
// `computed` source is server-resolved at fetch time (the SDK can't compute
// it locally), so its value flows through `defaultValue` from the payload.

import type { SurveyVariable } from '../_types/variables.js';

interface ResolveVariablesParams {
  variables: SurveyVariable[] | undefined;
  // Host page URL (passed through hydration.host.url) — read query params
  // from this so it reflects the page the user actually saw, not the iframe.
  hostUrl?: string;
  // Caller-supplied overrides — typically from Chirp.survey.open opts.variables.
  overrides?: Record<string, unknown>;
}

export function resolveSurveyVariables({
  variables,
  hostUrl,
  overrides,
}: ResolveVariablesParams): Record<string, unknown> {
  const resolved: Record<string, unknown> = {};
  if (!variables || variables.length === 0) {
    return overrides ? { ...overrides } : resolved;
  }

  const params = parseQueryString(hostUrl);

  for (const variable of variables) {
    if (!variable?.key) continue;
    let value: unknown = variable.defaultValue;
    if (variable.source === 'urlParam' && params) {
      const fromUrl = params.get(variable.key);
      if (fromUrl !== null) value = fromUrl;
    }
    // overrides win regardless of source — host-supplied values from
    // Chirp.survey.open are always intentional.
    if (overrides && Object.prototype.hasOwnProperty.call(overrides, variable.key)) {
      value = overrides[variable.key];
    }
    if (value !== undefined && value !== null) {
      resolved[variable.key] = value;
    }
  }

  // Pass-through for keys in overrides that weren't declared in the survey
  // schema. Keeps the door open for adhoc piping/conditions without forcing
  // the survey author to register every variable upfront.
  if (overrides) {
    for (const [key, value] of Object.entries(overrides)) {
      if (!(key in resolved) && value !== undefined && value !== null) {
        resolved[key] = value;
      }
    }
  }

  return resolved;
}

function parseQueryString(hostUrl?: string): URLSearchParams | null {
  const url = hostUrl ?? (typeof window !== 'undefined' ? window.location?.href : undefined);
  if (!url) return null;
  try {
    return new URL(url).searchParams;
  } catch {
    return null;
  }
}
